<script setup lang="ts">
import { AxiosError } from 'axios'
import Debug from 'debug'
import clone from 'lodash/clone'
import get from 'lodash/get'
import { useDialog } from 'primevue/usedialog'
import { RestaurantAreas, SelectedLocationWithCoords } from '~/lib/interfaces/restaurant'
import { imService } from '~/lib/services/im/im.service'
import MapGoogle from '~/components/map/MapGoogle.vue'
import { DLVModalAction, DLVModalState } from '~/lib/models/dlv-modal'
import { Address } from '~/lib/services/store/order/order.dto'
import { addressToBodyData } from '~/utils/favoriteUtils'
import { ToastType } from '~/lib/models/ui/toast'
import { GTMContentGroup } from '~/lib/models/gtm'

const props = defineProps<{
  state: DLVModalState
  selectedLocation: SelectedLocationWithCoords
  previousPage: () => void
}>()

const debug = Debug('app:dlvModal:DLVSelectRestaurant')

const emit = defineEmits(['address-selected'])

const { t } = useLocale()
const restaurantStore = useRestaurantStore()
const authStore = useAuthStore()
const restaurantActions = useRestaurantActions()
const countryCode = useCountry()
const {
  trackNoRider,
  trackNoCoverage,
  trackConfirmAddress,
  trackAddFavorites,
  trackPageView,
  trackOnCloseModal,
} = useGTM()
const { restaurantServiceRef } = useStoreService()
const restaurantService = restaurantServiceRef()
const { $toast } = useNuxtApp()
const showOverlay: Function = inject('showOverlay')!

const insideB = ref(true)
const mapGoogle = ref<InstanceType<typeof MapGoogle> | null>(null)
const dialog = useDialog()

const editingOrAddingFavoriteAddress = computed(() => {
  return (
    props.state.action === DLVModalAction.ONLY_ADD_FAVORITE ||
    props.state.action === DLVModalAction.ONLY_EDIT_FAVORITE_ADDRESS
  )
})

const deliveryAddress = computed(() => {
  return restaurantStore?.provisionalAddress
})

const resetStateEditingOrAddingFavorite = () => {
  restaurantStore.candidateArea = undefined
  restaurantStore.selectedGoogleLocation = undefined
  restaurantStore.provisionalAddress = undefined
  restaurantStore.closeChangeRestaurantModal()
  showOverlay(false)
  trackOnCloseModal()
}

const addAddressAsFavorite = async (
  provisionalAddress: Address
): Promise<'continue' | 'stop' | 'fail'> => {
  // If is a favorite address what im a saving add call to im to save address
  if (authStore.token && deliveryAddress.value?.alias) {
    try {
      trackAddFavorites()
      const addresses = await imService.saveFavoriteAddress({
        accessToken: authStore.token,
        address: addressToBodyData(provisionalAddress),
        country: countryCode,
      })
      authStore.user!.addresses = addresses
      const lastSavedAddressId = addresses[addresses.length - 1]?._id
      if (lastSavedAddressId && deliveryAddress.value)
        restaurantStore.provisionalAddress!.favoriteId = lastSavedAddressId
      return 'continue'
    } catch (err) {
      debug('🚀 ~ Error saving favorite address ~ err:', get(err, 'response.data') ?? err)

      const IMResponsecode: string = get(err, 'response.data.code', 'unknown')
      if (IMResponsecode === 'AddressesLimitReached') {
        showOverlay(false)
        $toast.show({
          type: ToastType.ERROR,
          content: t('profile.subSections.mySavedAddresses.toast.limitReached'),
          once: true,
        })
        restaurantStore.provisionalAddress = undefined
        if (props.state.action === DLVModalAction.SELECT_ADDRESS) {
          // stay on modalChangeRestaurant with DLV selected
          restaurantStore.closeModal()
        } else if (props.state.action === DLVModalAction.ONLY_ADD_FAVORITE) {
          // close modal change restaurant completely and stay on original page
          restaurantStore.closeChangeRestaurantModal()
          restaurantStore.closeModal()
          trackOnCloseModal()
        }
        return 'stop'
      }
      return 'fail'
    }
  }
  return 'fail'
}

const editAddressFavoriteAddress = async (provisionalAddress: Address) => {
  if (
    authStore.token &&
    deliveryAddress.value?.alias &&
    props.state.action === DLVModalAction.ONLY_EDIT_FAVORITE_ADDRESS
  ) {
    try {
      const addresses = await imService.editFavoriteAddress({
        accessToken: authStore.token,
        favoriteAddressId: props.state.address._id,
        address: addressToBodyData(provisionalAddress),
        country: countryCode,
      })
      authStore.user!.addresses = addresses
      return true
    } catch (err) {
      debug('🚀 ~ Error editing favorite address ~ err:', get(err, 'response.data') ?? err)
      return false
    }
  }
  return false
}

const handleContinue = async () => {
  // Get real location from MapGoogle pinCenter or stored coordinates
  const lat = mapGoogle.value?.pinCenter.lat || deliveryAddress.value?.latitude
  const lng = mapGoogle.value?.pinCenter.lng || deliveryAddress.value?.longitude

  if (!deliveryAddress.value || !lat || !lng) {
    restaurantStore.closeModal()
    return
  }
  // clone address
  const provisionalAddress = clone(deliveryAddress.value)

  try {
    // open overlay
    showOverlay(true)
    trackConfirmAddress()

    // and add pincenter coord
    provisionalAddress.latitude = lat
    provisionalAddress.longitude = lng

    const restaurant = await restaurantService.getRestaurantDelivery({
      latitude: String(lat),
      longitude: String(lng),
      countryCode,
      area: RestaurantAreas.DLV,
    })

    // currently editing address is already selected as current address (DLV, addressDLV)
    const willEditAddressIsCurrentAddress =
      !!props.state.address &&
      props.state.address._id === restaurantStore.selectedRestaurant?.addressDLV?.favoriteId

    if (editingOrAddingFavoriteAddress.value) {
      const provisionalAddressToEdit = clone(provisionalAddress)
      let processSuccess = false
      if (props.state.action === DLVModalAction.ONLY_EDIT_FAVORITE_ADDRESS) {
        // edit favorite only
        processSuccess = await editAddressFavoriteAddress(provisionalAddressToEdit)
      } else if (props.state.action === DLVModalAction.ONLY_ADD_FAVORITE) {
        // add favorite only
        const result = await addAddressAsFavorite(provisionalAddressToEdit)
        if (result === 'stop') return
        processSuccess = result === 'continue'
      }

      if (processSuccess) {
        $toast.show({
          type: ToastType.SUCCESS,
          content: t('profile.subSections.mySavedAddresses.toast.addNew'),
          once: true,
        })
      }

      if (!willEditAddressIsCurrentAddress) {
        resetStateEditingOrAddingFavorite()
        return
      }
    }

    // Get if order will be delivered by walking crew
    restaurantActions.setCrewStatus(restaurant?.isCrew)
    restaurantActions.setDeliveryDistance(restaurant.distance)

    // update restaurant store address with distance from restaurant
    provisionalAddress.distance = restaurant.distance
    restaurantStore.setAddress(provisionalAddress)
    // add favorites (ONLY SELECT_ADDRESS by the moment)
    if (!editingOrAddingFavoriteAddress.value) {
      const result = await addAddressAsFavorite(provisionalAddress)
      if (result === 'stop') return
    }
    restaurantStore.restaurantTips = restaurant.tips
    // setRestaurant(rest, false) => setRestaurant, loadCategories, check product param, navigate to restaurant home
    // setRestaurant(rest, true) => setRestaurant, loadCategories
    await restaurantActions.setRestaurant(restaurant, willEditAddressIsCurrentAddress)

    if (willEditAddressIsCurrentAddress) resetStateEditingOrAddingFavorite()
    emit('address-selected')
    // close overlay
    showOverlay(false)
  } catch (err: unknown) {
    if (err instanceof AxiosError) {
      // TODO: Change this to allow user to select restaurant for errors 613 and 618, when the backend updates the endpoint
      if (editingOrAddingFavoriteAddress.value) {
        await handleContinueErrorEditingOrAddingFavorite(err, provisionalAddress)
      } else await handleContinueErrorSelectAddress(err)
    } else {
      debug(err)
    }
    // close overlay
    showOverlay(false)
  }
}

const handleContinueErrorEditingOrAddingFavorite = async (
  err: AxiosError,
  provisionalAddress: Address
) => {
  const status = err?.response?.status ?? 500
  const permittedErrors = [613, 618]
  if (status === 605) {
    trackNoCoverage()
    trackPageView(
      {
        cg: GTMContentGroup.primary.USER,
        cg2: GTMContentGroup.secondary.FAVORITES,
        cg3: GTMContentGroup.tertiary.ADDRESS_NOT_AVAILABLE,
      },
      true
    )
    await showDialogAlert({
      dialog,
      data: {
        title: t('alertDialog.mcDeliveryError.title'),
        body: t('alertDialog.mcDeliveryError.body'),
        btnPrimary: t('common.alertAccept'),
        headerImage: '/images/icons/area/no_coverage.svg',
      },
      customCssClass: '[&_img]:!w-60',
    })
    resetStateEditingOrAddingFavorite()
    return
  } else if (!permittedErrors.includes(status)) {
    await showDialogAlert({
      dialog,
      data: {
        title: t('alertDialog.genericError.title'),
        body: t('alertDialog.genericError.body'),
        headerImage: '/images/icons/area/sign.svg',
      },
    })
    resetStateEditingOrAddingFavorite()
    return
  }

  let processSuccess = false
  if (props.state.action === DLVModalAction.ONLY_EDIT_FAVORITE_ADDRESS) {
    // edit favorite only
    processSuccess = await editAddressFavoriteAddress(provisionalAddress)
  } else if (props.state.action === DLVModalAction.ONLY_ADD_FAVORITE) {
    // add favorite only
    const result = await addAddressAsFavorite(provisionalAddress)
    if (result === 'stop') return
    processSuccess = result === 'continue'
  }
  if (processSuccess) {
    $toast.show({
      type: ToastType.SUCCESS,
      content: t('profile.subSections.mySavedAddresses.toast.addNew'),
      once: true,
    })
  }
  resetStateEditingOrAddingFavorite()
}

const handleContinueErrorSelectAddress = async (err: AxiosError) => {
  let result
  const status = err?.response?.status ?? 500
  if (status === 605) {
    trackNoCoverage()
    result = await showDialogAlert({
      dialog,
      data: {
        title: t('alertDialog.mcDeliveryError.title'),
        body: t('alertDialog.mcDeliveryError.body'),
        btnPrimary: t('alertDialog.mcDeliveryError.btnPrimary'),
        btnSecondary: t('alertDialog.mcDeliveryError.btnSecondary'),
        headerImage: '/images/icons/area/no_coverage.svg',
      },
      customCssClass: '[&_img]:!w-60',
    })
  } else if (status === 613) {
    trackNoRider()
    result = await showDialogAlert({
      dialog,
      data: {
        title: t('alertDialog.ridersError.title'),
        body: t('alertDialog.ridersError.body'),
        btnPrimary: t('alertDialog.ridersError.btnPrimary'),
        btnSecondary: t('alertDialog.ridersError.btnSecondary'),
        headerImage: '/images/icons/area/sign.svg',
      },
    })
  } else if (status === 618) {
    result = await showDialogAlert({
      dialog,
      data: {
        title: t('alertDialog.restaurantClosedError.title'),
        body: t('alertDialog.restaurantClosedError.body'),
        btnPrimary: t('alertDialog.restaurantClosedError.btnPrimary'),
        btnSecondary: t('alertDialog.restaurantClosedError.btnSecondary'),
        headerImage: '/images/icons/area/sign.svg',
      },
    })
  } else {
    result = await showDialogAlert({
      dialog,
      data: {
        title: t('alertDialog.genericError.title'),
        body: t('alertDialog.genericError.body'),
        headerImage: '/images/icons/area/sign.svg',
      },
    })
  }

  if (result) {
    restaurantStore.closeModal()
    await restaurantStore.setArea({ area: RestaurantAreas.MOP, updateGoogleMaps: true })
    restaurantStore.openModal()
  } else {
    restaurantStore.closeModal()
  }
}

const insideBounds = () => {
  return {
    value: insideB.value,
    setInsideBounds: (value: boolean) => {
      insideB.value = value
    },
  }
}
</script>

<template>
  <ModalCenter :content-full="true" :add-close-button="false">
    <template #header>
      <div class="flex items-center gap-2">
        <img
          class="flex-start cursor-pointer"
          src="/images/icons/arrowLeft.svg"
          alt="arrowLeft"
          @click="previousPage"
        />
        <p class="md:hidden">{{ t('selectRestaurant.DLV.willSendTo') }}</p>
      </div>
    </template>
    <template #content>
      <div
        data-test="dlv-modal-select"
        class="mx-4 mb-8 mt-8 flex justify-between text-left md:mx-14 md:mt-10"
      >
        <div class="w-xs flex flex-col gap-3">
          <p class="hidden text-[10px] text-mcd-secondaryDarkGrey md:block">
            {{ t('selectRestaurant.DLV.willSendTo') }}
          </p>
          <div class="flex max-w-xs">
            <img src="/images/icons/location.svg" class="place-self-start" alt="location" />
            <div class="flex flex-col gap-4">
              <p class="place-self-start text-lg font-bold">
                {{ deliveryAddress?.address }}
                {{ deliveryAddress?.number }}
                {{ deliveryAddress?.postalCode }},
                {{ deliveryAddress?.city }}
              </p>
              <p v-if="deliveryAddress?.complement" class="text-[10px]">
                {{ deliveryAddress?.complement }}
              </p>
            </div>
          </div>
        </div>
        <McdButton
          :disabled="!insideB"
          data-test="dlv-confirm-location-button"
          is-primary
          class="hidden h-fit self-end md:block"
          @click="handleContinue"
        >
          {{ t('selectRestaurant.DLV.confirmLocation') }}
        </McdButton>
      </div>
      <div class="w-full flex-1">
        <MapGoogle
          ref="mapGoogle"
          :selected-location="selectedLocation"
          :inside-bounds="insideBounds()"
        />
      </div>
    </template>
    <template #actions>
      <div class="mr-14 w-full md:hidden">
        <McdButton
          data-test="dlv-confirm-location-button--m"
          :disabled="!insideB"
          is-primary
          class="btn-primary ml-auto mr-0 h-16 w-full text-sm"
          @on-click="handleContinue"
        >
          {{ t('selectRestaurant.DLV.confirmLocation') }}
        </McdButton>
      </div>
    </template>
  </ModalCenter>
</template>
